import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { exporterPlanningPDF, exporterPlanningExcel } from '../utils/exportPlanning';
import { formatDuree, calculerDureeCreneauMinutes, formatHeuresMinutes } from '../utils/formatDuree';
import JsBarcode from 'jsbarcode';
import { Html5QrcodeScanner } from 'html5-qrcode';
import CommentairesPanel from './CommentairesPanel';

function PlanningProductionV2() {
  const [semaineCourante, setSemaineCourante] = useState(getCurrentWeek());
  const [cartes, setCartes] = useState([]);
  const [cartesGroupees, setCartesGroupees] = useState({});
  const [groupesDepliés, setGroupesDepliés] = useState({});
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedCard, setDraggedCard] = useState(null);
  const [statistiques, setStatistiques] = useState({
    totalHeures: 0,
    nombreCartes: 0,
    nombreProduits: 0
  });
  const [capaciteHebdo, setCapaciteHebdo] = useState(0); // en minutes
  
  // États pour la modal de création
  const [showModal, setShowModal] = useState(false);
  const [typesProduits, setTypesProduits] = useState([]);
  const [formData, setFormData] = useState({
    type_produit_id: '',
    nom_attribue: '',
    quantite: 1,
    date_creation: new Date().toISOString().split('T')[0]
  });

  // États pour la modal de validation
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationStep, setValidationStep] = useState(1);
  const [validationModeGroupe, setValidationModeGroupe] = useState(false); // Mode validation groupée
  const [showScanner, setShowScanner] = useState(false);
  const [scannerType, setScannerType] = useState(null);
  const scannerRef = useRef(null);
  const [validationData, setValidationData] = useState({
    operateurCode: '',
    operateur: null,
    carteCode: '',
    carte: null,
    operationIds: [],  // Array pour stocker plusieurs IDs d'opérations
    cartesGroupe: [],  // Pour validation groupée : toutes les cartes du groupe
    typeProduitNom: '', // Nom du type de produit du groupe
    typeProduitId: ''   // ID du type de produit
  });

  // État pour la modal de progression détaillée
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedCarteForProgress, setSelectedCarteForProgress] = useState(null);

  // États pour la recherche globale
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [highlightedCarteId, setHighlightedCarteId] = useState(null);

  // États pour le report de cartes
  const [showReportModal, setShowReportModal] = useState(false);
  const [cartesEnRetard, setCartesEnRetard] = useState([]);
  const [showRappelLundi, setShowRappelLundi] = useState(false);

  // États pour la modification de date d'expédition
  const [showDateModificationModal, setShowDateModificationModal] = useState(false);
  const [carteEnCoursModification, setCarteEnCoursModification] = useState(null);
  const [nouvelleDate, setNouvelleDate] = useState('');
  const [nouvelleSemaine, setNouvelleSemaine] = useState(null);
  const [changementSemaine, setChangementSemaine] = useState(false);

  // États pour le panneau de commentaires
  const [showCommentairesPanel, setShowCommentairesPanel] = useState(false);
  const [carteSelectionneeCommentaires, setCarteSelectionneeCommentaires] = useState(null);
  const [operateurConnecte, setOperateurConnecte] = useState(null);

  // États pour la modification/suppression de carte
  const [showModalModification, setShowModalModification] = useState(false);
  const [carteEnModification, setCarteEnModification] = useState(null);
  const [cartesGroupeModification, setCartesGroupeModification] = useState([]);
  const [showModalSuppression, setShowModalSuppression] = useState(false);
  const [showModalGraphisme, setShowModalGraphisme] = useState(false);
  const [carteGraphisme, setCarteGraphisme] = useState(null);
  const [cartesGroupeGraphisme, setCartesGroupeGraphisme] = useState([]);
  const [formModification, setFormModification] = useState({
    nom_attribue: '',
    date_creation: ''
  });

  useEffect(() => {
    loadCartesSemaine();
    loadTypesProduits();
    loadCapaciteHebdo();
    verifierRappelLundi();
  }, [semaineCourante]);

  // Vérifier si c'est lundi matin et afficher le rappel
  useEffect(() => {
    const interval = setInterval(() => {
      verifierRappelLundi();
    }, 60000); // Vérifier toutes les minutes

    return () => clearInterval(interval);
  }, []);

  // Au montage, vérifier si on doit ouvrir un commentaire depuis une notification
  useEffect(() => {
    const carteIdAOuvrir = localStorage.getItem('ouvrir_commentaire_carte');
    if (carteIdAOuvrir) {
      // Nettoyer le localStorage
      localStorage.removeItem('ouvrir_commentaire_carte');
      // Charger et ouvrir la carte
      chargerEtOuvrirCarte(carteIdAOuvrir);
    }
  }, []);

  // Écouter les événements pour ouvrir les commentaires depuis les notifications
  useEffect(() => {
    const handleEventOuvrirCommentaires = (event) => {
      const { carteId } = event.detail;
      
      // Trouver la carte dans la liste
      const carte = cartes.find(c => c.id === carteId);
      if (carte) {
        handleOuvrirCommentaires(carte);
      } else {
        // Charger la carte depuis la base si elle n'est pas dans la semaine courante
        chargerEtOuvrirCarte(carteId);
      }
    };

    window.addEventListener('ouvrir-commentaires', handleEventOuvrirCommentaires);
    return () => window.removeEventListener('ouvrir-commentaires', handleEventOuvrirCommentaires);
  }, [cartes]);

  // Fonction pour charger une carte spécifique et ouvrir ses commentaires
  const chargerEtOuvrirCarte = async (carteId) => {
    try {
      const { data, error } = await supabase
        .from('cartes_production')
        .select(`
          *,
          types_produits (
            id,
            nom,
            reference
          )
        `)
        .eq('id', carteId)
        .single();

      if (error) throw error;
      
      if (data) {
        handleOuvrirCommentaires(data);
      }
    } catch (error) {
      console.error('Erreur chargement carte:', error);
      alert('Impossible de charger la carte pour afficher les commentaires');
    }
  };

  function getCurrentWeek() {
    const now = new Date();
    return getWeekNumber(now);
  }

  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  function getWeekDates(weekNumber) {
    const year = new Date().getFullYear();
    const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) {
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    } else {
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    }
    const ISOweekEnd = new Date(ISOweekStart);
    ISOweekEnd.setDate(ISOweekStart.getDate() + 4);

    const formatDate = (date) => {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    return `${formatDate(ISOweekStart)} - ${formatDate(ISOweekEnd)}`;
  }

  const loadCapaciteHebdo = async () => {
    try {
      // Charger les créneaux planning avec les infos opérateur et profil
      const { data: plannings, error } = await supabase
        .from('planning_operateurs')
        .select('jour_semaine, heure_debut, heure_fin, operateur_id, operateurs (inclus_dans_planning, profils (inclus_dans_planning))');

      if (error) {
        console.error('Erreur chargement capacité:', error);
        return;
      }

      // Ne compter que les créneaux des opérateurs inclus dans le planning
      const totalMinutes = (plannings || []).reduce((sum, creneau) => {
        // Logique : override opérateur > profil
        const overrideOperateur = creneau.operateurs?.inclus_dans_planning;
        const profilInclus = creneau.operateurs?.profils?.inclus_dans_planning || false;
        const estInclus = overrideOperateur !== null && overrideOperateur !== undefined
          ? overrideOperateur
          : profilInclus;

        if (!estInclus) return sum;

        return sum + calculerDureeCreneauMinutes(
          creneau.heure_debut.substring(0, 5),
          creneau.heure_fin.substring(0, 5)
        );
      }, 0);

      setCapaciteHebdo(totalMinutes);
    } catch (error) {
      console.error('Erreur calcul capacité:', error);
    }
  };

  const loadCartesSemaine = async () => {
    const { data, error } = await supabase
      .from('cartes_production')
      .select(`
        *,
        types_produits (
          id,
          nom,
          delai_max_jours
        )
      `)
      .eq('semaine_planifiee', semaineCourante)
      // Pas de filtre sur statut : on charge toutes les cartes (en_cours ET terminee)
      .order('date_creation', { ascending: true });

    if (error) {
      console.error('Erreur chargement cartes:', error);
      return;
    }

    // Charger les suivis pour chaque carte
    const cartesAvecSuivis = await Promise.all(
      (data || []).map(async (carte) => {
        const { data: suivis } = await supabase
          .from('suivi_operations')
          .select(`
            *,
            operations (
              id,
              nom,
              ordre,
              temps_realisation,
              inclus_dans_planning
            )
          `)
          .eq('carte_production_id', carte.id)
          .order('id', { ascending: true });

        return {
          ...carte,
          suivis: suivis || []
        };
      })
    );

    setCartes(cartesAvecSuivis);

    // Grouper par type de produit
    const grouped = cartesAvecSuivis.reduce((acc, carte) => {
      const produitNom = carte.types_produits?.nom || 'Sans produit';
      if (!acc[produitNom]) {
        acc[produitNom] = [];
      }
      acc[produitNom].push(carte);
      return acc;
    }, {});

    // Trier les cartes dans chaque groupe selon les règles
    Object.keys(grouped).forEach(produitNom => {
      grouped[produitNom] = trierCartes(grouped[produitNom]);
    });

    setCartesGroupees(grouped);

    // Déplier tous les groupes par défaut
    const depliesInitiaux = {};
    Object.keys(grouped).forEach(produitNom => {
      depliesInitiaux[produitNom] = true;
    });
    setGroupesDepliés(depliesInitiaux);

    // Calculer les statistiques
    let totalSecondes = 0;
    cartesAvecSuivis.forEach(carte => {
      const secondesRestantes = calculerTempsRestant(carte);
      totalSecondes += secondesRestantes;
    });

    setStatistiques({
      totalHeures: totalSecondes,
      nombreCartes: cartesAvecSuivis.length,
      nombreProduits: Object.keys(grouped).length
    });
  };

  const loadTypesProduits = async () => {
    const { data, error } = await supabase
      .from('types_produits')
      .select('*')
      .order('nom', { ascending: true });
    
    if (error) {
      console.error('Erreur chargement types produits:', error);
    } else {
      setTypesProduits(data || []);
    }
  };

  const generateCodeBarre = () => {
    const chars = '0123456789BCDEFGHJKLNPRSTUVXY';
    let code = 'PROD';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const calculateDateExpedition = (delai_max_jours, dateCreation) => {
    const date = new Date(dateCreation);
    date.setDate(date.getDate() + delai_max_jours);
    return date.toISOString().split('T')[0];
  };

  const openModal = () => {
    setFormData({
      type_produit_id: '',
      nom_attribue: '',
      quantite: 1,
      date_creation: new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      type_produit_id: '',
      nom_attribue: '',
      quantite: 1,
      date_creation: new Date().toISOString().split('T')[0]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const produit = typesProduits.find(p => p.id === formData.type_produit_id);
    if (!produit) return;
    
    const quantite = formData.quantite || 1;
    
    // Confirmation si quantité > 1
    if (quantite > 1) {
      const confirmation = window.confirm(
        `Vous allez créer ${quantite} cartes de production avec les noms suivants :\n\n` +
        Array.from({length: quantite}, (_, i) => `• ${formData.nom_attribue} ${i + 1}/${quantite}`).join('\n') +
        `\n\nConfirmer la création ?`
      );
      
      if (!confirmation) return;
    }
    
    // Créer les cartes
    for (let i = 1; i <= quantite; i++) {
      const codeBarre = generateCodeBarre();
      const dateExpeditionMax = calculateDateExpedition(produit.delai_max_jours, formData.date_creation);
      
      const nomCarte = quantite > 1 
        ? `${formData.nom_attribue} ${i}/${quantite}`
        : formData.nom_attribue;
      
      const { data: nouvelleCarte, error } = await supabase
        .from('cartes_production')
        .insert([{
          type_produit_id: formData.type_produit_id,
          nom_attribue: nomCarte,
          code_barre: codeBarre,
          date_expedition_max: dateExpeditionMax,
          date_creation: formData.date_creation,
          statut: 'en_cours',
          semaine_planifiee: semaineCourante
        }])
        .select()
        .single();
      
      if (error) {
        console.error('Erreur création carte:', error);
        alert(`Erreur lors de la création de la carte ${i}/${quantite}`);
        break;
      } else {
        // Créer les entrées de suivi pour chaque opération
        const { data: operations } = await supabase
          .from('operations')
          .select('id')
          .eq('type_produit_id', formData.type_produit_id)
          .order('ordre', { ascending: true });
        
        if (operations) {
          const suivis = operations.map(op => ({
            carte_production_id: nouvelleCarte.id,
            operation_id: op.id,
            validee: false
          }));
          
          await supabase.from('suivi_operations').insert(suivis);
        }
      }
    }
    
    loadCartesSemaine();
    closeModal();
  };

  // Fonctions pour la validation d'opération
  const openValidationModal = () => {
    setShowValidationModal(true);
    setValidationStep(1);
    setValidationModeGroupe(false);
    setValidationData({
      operateurCode: '',
      operateur: null,
      carteCode: '',
      carte: null,
      operationIds: [],
      cartesGroupe: [],
      typeProduitNom: '',
      typeProduitId: ''
    });
  };

  // Nouvelle fonction pour ouvrir la validation groupée
  const openValidationGroupeeModal = async (produitNom, cartesGroupe) => {
    // Filtrer uniquement les cartes éligibles (en_attente, en_cours)
    const cartesEligibles = cartesGroupe.filter(carte => 
      carte.statut === 'en_attente' || carte.statut === 'en_cours'
    );

    if (cartesEligibles.length === 0) {
      alert('⚠️ Aucune carte éligible pour la validation groupée.\nToutes les cartes sont soit terminées, soit annulées.');
      return;
    }

    // Récupérer le type de produit (toutes les cartes du groupe ont le même type)
    const typeProduitId = cartesEligibles[0]?.type_produit_id;
    
    // Charger les opérations du type de produit
    const { data: operations } = await supabase
      .from('operations')
      .select('*')
      .eq('type_produit_id', typeProduitId)
      .order('ordre', { ascending: true });

    // Charger les suivis pour toutes les cartes du groupe
    const cartesIds = cartesEligibles.map(c => c.id);
    const { data: suivis } = await supabase
      .from('suivi_operations')
      .select('*')
      .in('carte_production_id', cartesIds);

    // Associer les suivis à chaque carte
    const cartesAvecSuivis = cartesEligibles.map(carte => ({
      ...carte,
      operations: operations || [],
      suivis: (suivis || []).filter(s => s.carte_production_id === carte.id)
    }));
    
    setShowValidationModal(true);
    setValidationStep(1);
    setValidationModeGroupe(true);
    setValidationData({
      operateurCode: '',
      operateur: null,
      carteCode: '',
      carte: null,
      operationIds: [],
      cartesGroupe: cartesAvecSuivis,
      typeProduitNom: produitNom,
      typeProduitId: typeProduitId
    });
  };

  const closeValidationModal = () => {
    setShowValidationModal(false);
    setValidationStep(1);
    setValidationModeGroupe(false);
    setShowScanner(false);
    setScannerType(null);
    if (scannerRef.current) {
      scannerRef.current.clear();
      scannerRef.current = null;
    }
    setValidationData({
      operateurCode: '',
      operateur: null,
      carteCode: '',
      carte: null,
      operationIds: [],
      cartesGroupe: [],
      typeProduitNom: '',
      typeProduitId: ''
    });
  };

  const startScanner = (type) => {
    setScannerType(type);
    setShowScanner(true);
    
    setTimeout(() => {
      const scannerId = type === 'operateur' ? 'scanner-operateur' : 'scanner-carte';
      
      if (scannerRef.current) {
        scannerRef.current.clear();
      }

      const scanner = new Html5QrcodeScanner(scannerId, {
        fps: 10,
        qrbox: { width: 250, height: 150 },
        formatsToSupport: [0, 1, 2, 3, 4, 5, 6, 7, 8]
      });

      scanner.render(
        async (decodedText) => {
          scanner.clear();
          setShowScanner(false);
          setScannerType(null);
          scannerRef.current = null;
          
          if (type === 'operateur') {
            const { data, error } = await supabase
              .from('operateurs')
              .select('*, profils(nom)')
              .eq('code_barre', decodedText.trim())
              .single();

            if (error || !data) {
              alert('Opérateur non trouvé. Vérifiez le code-barre.');
              return;
            }

            setValidationData(prev => ({ ...prev, operateur: data, operateurCode: decodedText }));
            setValidationStep(2);
          } else if (type === 'carte') {
            const { data, error } = await supabase
              .from('cartes_production')
              .select(`
                *,
                types_produits (nom, delai_max_jours)
              `)
              .eq('code_barre', decodedText.trim())
              .single();

            if (error || !data) {
              alert('Carte de production non trouvée.');
              return;
            }

            const { data: operations } = await supabase
              .from('operations')
              .select('*')
              .eq('type_produit_id', data.type_produit_id)
              .order('ordre', { ascending: true });

            const { data: suivis } = await supabase
              .from('suivi_operations')
              .select('*')
              .eq('carte_production_id', data.id);

            const carteComplete = {
              ...data,
              operations: operations || [],
              suivis: suivis || []
            };

            setValidationData(prev => ({ ...prev, carte: carteComplete, carteCode: decodedText }));
            setValidationStep(3);
          }
        },
        (error) => {}
      );

      scannerRef.current = scanner;
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear();
      scannerRef.current = null;
    }
    setShowScanner(false);
    setScannerType(null);
  };

  const handleScanOperateur = async () => {
    if (!validationData.operateurCode.trim()) {
      alert('Veuillez saisir ou scanner un code-barre opérateur');
      return;
    }

    const { data, error } = await supabase
      .from('operateurs')
      .select('*, profils(nom)')
      .eq('code_barre', validationData.operateurCode.trim())
      .single();

    if (error || !data) {
      alert('Opérateur non trouvé. Vérifiez le code-barre.');
      return;
    }

    setValidationData({ ...validationData, operateur: data });
    
    // Si mode groupé, sauter l'étape 2 et aller directement à l'étape 3
    if (validationModeGroupe) {
      setValidationStep(3);
    } else {
      setValidationStep(2);
    }
  };

  const handleScanCarte = async () => {
    if (!validationData.carteCode.trim()) {
      alert('Veuillez saisir ou scanner un code-barre de carte');
      return;
    }

    const { data, error } = await supabase
      .from('cartes_production')
      .select(`
        *,
        types_produits (nom, delai_max_jours)
      `)
      .eq('code_barre', validationData.carteCode.trim())
      .single();

    if (error || !data) {
      alert('Carte de production non trouvée.');
      return;
    }

    const { data: operations } = await supabase
      .from('operations')
      .select('*')
      .eq('type_produit_id', data.type_produit_id)
      .order('ordre', { ascending: true });

    const { data: suivis } = await supabase
      .from('suivi_operations')
      .select('*')
      .eq('carte_production_id', data.id);

    const carteComplete = {
      ...data,
      operations: operations || [],
      suivis: suivis || []
    };

    setValidationData({ ...validationData, carte: carteComplete, operationIds: [] });
    setValidationStep(3);
  };

  const handleSearchGlobal = async (searchValue) => {
    setSearchTerm(searchValue);
    
    if (searchValue.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // Rechercher dans TOUTES les cartes, pas seulement la semaine en cours
    const searchLower = searchValue.toLowerCase();
    const { data, error } = await supabase
      .from('cartes_production')
      .select(`
        *,
        types_produits (
          id,
          nom,
          delai_max_jours
        )
      `)
      .order('semaine_planifiee', { ascending: false })
      .order('date_creation', { ascending: false });

    if (error) {
      console.error('Erreur recherche:', error);
      return;
    }

    // Filtrer les résultats
    const results = (data || []).filter(carte => {
      return (
        carte.nom_attribue?.toLowerCase().includes(searchLower) ||
        carte.code_barre?.toLowerCase().includes(searchLower) ||
        carte.types_produits?.nom?.toLowerCase().includes(searchLower)
      );
    });

    setSearchResults(results.slice(0, 10)); // Limiter à 10 résultats
    setShowSearchResults(results.length > 0);
  };

  const handleSelectSearchResult = (carte) => {
    // Naviguer vers la semaine de la carte
    setSemaineCourante(carte.semaine_planifiee);
    
    // Mettre en surbrillance la carte
    setHighlightedCarteId(carte.id);
    
    // Cacher les résultats
    setShowSearchResults(false);
    
    // Retirer la surbrillance après 3 secondes
    setTimeout(() => {
      setHighlightedCarteId(null);
    }, 3000);
  };

  const handleOpenProgressModal = async (carte) => {
    // Charger les opérations avec les détails des opérateurs
    const { data: suivis } = await supabase
      .from('suivi_operations')
      .select(`
        *,
        operations (
          id,
          nom,
          ordre,
          temps_realisation
        ),
        operateurs (
          prenom,
          nom
        )
      `)
      .eq('carte_production_id', carte.id);

    // Trier les suivis par l'ordre chronologique des opérations
    const suivisTries = (suivis || []).sort((a, b) => {
      const ordreA = a.operations?.ordre || 999;
      const ordreB = b.operations?.ordre || 999;
      return ordreA - ordreB;
    });

    const carteComplete = {
      ...carte,
      suivis: suivisTries
    };

    setSelectedCarteForProgress(carteComplete);
    setShowProgressModal(true);
  };

  const handleValidateOperation = async () => {
    if (!validationData.operationIds || validationData.operationIds.length === 0) {
      alert('Veuillez sélectionner au moins une opération');
      return;
    }

    try {
      const now = new Date().toISOString();

      // Valider chaque opération sélectionnée
      for (const operationId of validationData.operationIds) {
        // ⚠️ IMPORTANT : Recharger le suivi en temps réel pour éviter les doublons
        const { data: suiviActuel } = await supabase
          .from('suivi_operations')
          .select('*')
          .eq('carte_production_id', validationData.carte.id)
          .eq('operation_id', operationId)
          .maybeSingle();

        if (suiviActuel) {
          const updateData = {
            operateur_id: validationData.operateur.id,
            validee: true,
            statut: 'terminee',
            date_fin: now
          };

          if (!suiviActuel.date_debut) {
            updateData.date_debut = now;
          }

          const { error } = await supabase
            .from('suivi_operations')
            .update(updateData)
            .eq('id', suiviActuel.id);

          if (error) {
            alert(`Erreur lors de la validation de l'opération: ${error.message}`);
            return;
          }
        } else {
          const { error } = await supabase
            .from('suivi_operations')
            .insert([{
              carte_production_id: validationData.carte.id,
              operation_id: operationId,
              operateur_id: validationData.operateur.id,
              validee: true,
              statut: 'terminee',
              date_debut: now,
              date_fin: now
            }]);

          if (error) {
            alert(`Erreur lors de la validation de l'opération: ${error.message}`);
            return;
          }
        }
      }

      // Vérifier si toutes les opérations sont validées
      const { data: tousSuivis } = await supabase
        .from('suivi_operations')
        .select('*')
        .eq('carte_production_id', validationData.carte.id);

      if (tousSuivis) {
        const toutesValidees = tousSuivis.every(suivi => suivi.validee === true);
        
        if (toutesValidees) {
          const semaineCourante = getCurrentWeek();
          const semaineCartePlanifiee = validationData.carte.semaine_planifiee;
          
          // Déplacement automatique si terminée EN AVANCE
          if (semaineCourante < semaineCartePlanifiee) {
            await supabase
              .from('cartes_production')
              .update({ 
                statut: 'terminee',
                semaine_planifiee: semaineCourante, // Déplacer vers semaine courante
                date_fin: new Date().toISOString()
              })
              .eq('id', validationData.carte.id);

            alert(`✅ ${validationData.operationIds.length} opération(s) validée(s) avec succès !\n🎉 Toutes les opérations sont terminées.\n🚀 La carte planifiée semaine ${semaineCartePlanifiee} a été déplacée en semaine ${semaineCourante} (terminée en avance).`);
          } else {
            // Terminée à l'heure ou en retard : reste dans sa semaine
            await supabase
              .from('cartes_production')
              .update({ 
                statut: 'terminee',
                date_fin: new Date().toISOString()
              })
              .eq('id', validationData.carte.id);

            alert(`✅ ${validationData.operationIds.length} opération(s) validée(s) avec succès !\n🎉 Toutes les opérations sont terminées, la carte est automatiquement archivée.`);
          }
        } else {
          alert(`✅ ${validationData.operationIds.length} opération(s) validée(s) avec succès !`);
        }
      }

      closeValidationModal();
      await loadCartesSemaine();
    } catch (error) {
      alert(`Erreur inattendue: ${error.message}`);
    }
  };

  // Nouvelle fonction pour validation groupée
  const handleValidateOperationGroupee = async () => {
    if (!validationData.operationIds || validationData.operationIds.length === 0) {
      alert('Veuillez sélectionner au moins une opération');
      return;
    }

    if (!validationData.cartesGroupe || validationData.cartesGroupe.length === 0) {
      alert('Aucune carte à traiter');
      return;
    }

    try {
      const now = new Date().toISOString();
      let compteurValidations = 0;
      let compteurIgnores = 0;
      const totalCartes = validationData.cartesGroupe.length;
      const totalOperations = validationData.operationIds.length;
      let carteIndex = 0;

      // Créer un état de progression (on pourrait ajouter un loader ici)
      for (const carte of validationData.cartesGroupe) {
        carteIndex++;
        
        // Pour chaque opération sélectionnée
        for (const operationId of validationData.operationIds) {
          // ⚠️ IMPORTANT : Recharger le suivi en temps réel pour éviter les doublons
          const { data: suiviActuel } = await supabase
            .from('suivi_operations')
            .select('*')
            .eq('carte_production_id', carte.id)
            .eq('operation_id', operationId)
            .maybeSingle();

          // ⚠️ RÈGLE : Ne valider QUE si pas déjà validée
          if (suiviActuel && suiviActuel.validee === true) {
            compteurIgnores++;
            continue; // Skip, on conserve la validation existante
          }

          if (suiviActuel) {
            // Mettre à jour le suivi existant
            const updateData = {
              operateur_id: validationData.operateur.id,
              validee: true,
              statut: 'terminee',
              date_fin: now
            };

            if (!suiviActuel.date_debut) {
              updateData.date_debut = now;
            }

            const { error } = await supabase
              .from('suivi_operations')
              .update(updateData)
              .eq('id', suiviActuel.id);

            if (error) {
              alert(`Erreur lors de la validation de l'opération sur la carte ${carte.nom_attribue}: ${error.message}`);
              return;
            }
          } else {
            // Créer un nouveau suivi uniquement s'il n'existe vraiment pas
            const { error } = await supabase
              .from('suivi_operations')
              .insert([{
                carte_production_id: carte.id,
                operation_id: operationId,
                operateur_id: validationData.operateur.id,
                validee: true,
                statut: 'terminee',
                date_debut: now,
                date_fin: now
              }]);

            if (error) {
              alert(`Erreur lors de la validation de l'opération sur la carte ${carte.nom_attribue}: ${error.message}`);
              return;
            }
          }

          compteurValidations++;
        }

        // Vérifier si toutes les opérations de la carte sont validées
        const { data: tousSuivisCarte } = await supabase
          .from('suivi_operations')
          .select('*')
          .eq('carte_production_id', carte.id);

        if (tousSuivisCarte) {
          const toutesValidees = tousSuivisCarte.every(suivi => suivi.validee === true);
          
          if (toutesValidees && carte.statut !== 'terminee') {
            await supabase
              .from('cartes_production')
              .update({ 
                statut: 'terminee',
                date_fin: now
              })
              .eq('id', carte.id);
          }
        }
      }

      // Message final avec statistiques détaillées
      let message = `✅ Validation groupée terminée !\n\n`;
      message += `📦 Groupe : ${validationData.typeProduitNom}\n`;
      message += `👤 Opérateur : ${validationData.operateur.prenom} ${validationData.operateur.nom}\n`;
      message += `✓ ${compteurValidations} opération(s) validée(s) sur ${totalCartes} carte(s)\n`;
      
      if (compteurIgnores > 0) {
        message += `⏭️ ${compteurIgnores} opération(s) ignorée(s) (déjà validées)\n`;
      }
      
      const cartesExclues = cartesGroupees[validationData.typeProduitNom]?.length - totalCartes || 0;
      if (cartesExclues > 0) {
        message += `🚫 ${cartesExclues} carte(s) ignorée(s) (terminées ou annulées)`;
      }

      alert(message);

      closeValidationModal();
      await loadCartesSemaine();
    } catch (error) {
      alert(`Erreur inattendue: ${error.message}`);
    }
  };

  const trierCartes = (cartesListe) => {
    return [...cartesListe].sort((a, b) => {
      const badgeA = getBadgeCouleur(a);
      const badgeB = getBadgeCouleur(b);

      // Ordre de priorité : en_retard > urgent > en_cours > termine
      const priorite = {
        'en_retard': 1,
        'urgent': 2,
        'en_cours': 3,
        'termine': 4
      };

      const prioriteA = priorite[badgeA.statut] || 5;
      const prioriteB = priorite[badgeB.statut] || 5;

      if (prioriteA !== prioriteB) {
        return prioriteA - prioriteB;
      }

      // Si même statut, trier par date de création
      return new Date(a.date_creation) - new Date(b.date_creation);
    });
  };

  const calculerProgression = (carte) => {
    if (!carte.suivis || carte.suivis.length === 0) return { validees: 0, totales: 0, pourcentage: 0 };
    
    const validees = carte.suivis.filter(s => s.validee).length;
    const totales = carte.suivis.length;
    const pourcentage = totales > 0 ? Math.round((validees / totales) * 100) : 0;
    
    return { validees, totales, pourcentage };
  };

  const getEtapeEnCours = (carte) => {
    if (!carte.suivis || carte.suivis.length === 0) return 'Aucune opération';
    
    const progression = calculerProgression(carte);
    if (progression.pourcentage === 100) return 'Terminé';
    
    // Trouver la première opération non validée
    const etapeEnCours = carte.suivis.find(s => !s.validee);
    return etapeEnCours?.operations?.nom || 'En attente';
  };

  const calculerTempsRestant = (carte) => {
    if (!carte.suivis || carte.suivis.length === 0) return 0;
    
    return carte.suivis
      .filter(s => !s.validee && s.operations?.inclus_dans_planning !== false)
      .reduce((sum, s) => sum + (s.operations?.temps_realisation || 0), 0);
  };

  const calculerSecondesGroupe = (cartesListe) => {
    return cartesListe.reduce((sum, carte) => sum + calculerTempsRestant(carte), 0);
  };

  const getBadgeCouleur = (carte) => {
    const { validees, totales } = calculerProgression(carte);
    
    // Terminé
    if (validees === totales && totales > 0) {
      return { emoji: '🟢', label: 'Terminé', color: '#10b981', bgColor: '#d1fae5', statut: 'termine' };
    }

    // Vérifier la date d'expédition
    if (carte.date_expedition_max) {
      const aujourdhui = new Date();
      aujourdhui.setHours(0, 0, 0, 0);
      const dateFin = new Date(carte.date_expedition_max);
      dateFin.setHours(0, 0, 0, 0);
      const joursRestants = Math.ceil((dateFin - aujourdhui) / (1000 * 60 * 60 * 24));

      if (joursRestants < 0) {
        return { emoji: '🔴', label: 'En retard', color: '#ef4444', bgColor: '#fee2e2', statut: 'en_retard' };
      }
      if (joursRestants <= 2) {
        return { emoji: '🟡', label: 'Urgent', color: '#f59e0b', bgColor: '#fef3c7', statut: 'urgent' };
      }
    }

    return { emoji: '⚪', label: 'En cours', color: '#6b7280', bgColor: '#f3f4f6', statut: 'en_cours' };
  };

  const handleChangeSemaine = (direction) => {
    let nouvelleSemaine = semaineCourante + direction;
    
    if (nouvelleSemaine < 1) {
      nouvelleSemaine = 52;
    } else if (nouvelleSemaine > 52) {
      nouvelleSemaine = 1;
    }
    
    setSemaineCourante(nouvelleSemaine);
  };

  const toggleGroupe = (produitNom) => {
    setGroupesDepliés(prev => ({
      ...prev,
      [produitNom]: !prev[produitNom]
    }));
  };

  const filtrerCartes = (cartesListe) => {
    let filtered = cartesListe;
    
    // Filtre par graphisme à faire
    if (filtreStatut === 'graphisme_a_faire') {
      filtered = filtered.filter(carte => !carte.graphisme_pret);
      return filtered;
    }
    
    // Filtre par production non commencée (progression = 0%)
    if (filtreStatut === 'production_non_commencee') {
      filtered = filtered.filter(carte => calculerProgression(carte).pourcentage === 0);
      return filtered;
    }
    
    // Filtre par statut
    if (filtreStatut !== 'tous') {
      filtered = filtered.filter(carte => {
        const badge = getBadgeCouleur(carte);
        return badge.statut === filtreStatut;
      });
    }
    
    // Filtre par recherche
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(carte => {
        return (
          carte.nom_attribue?.toLowerCase().includes(searchLower) ||
          carte.code_barre?.toLowerCase().includes(searchLower) ||
          carte.types_produits?.nom?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    return filtered;
  };

  const compterCartesParStatut = (statut) => {
    if (statut === 'tous') return cartes.length;
    if (statut === 'graphisme_a_faire') return cartes.filter(c => !c.graphisme_pret).length;
    if (statut === 'production_non_commencee') return cartes.filter(c => calculerProgression(c).pourcentage === 0).length;
    return cartes.filter(c => getBadgeCouleur(c).statut === statut).length;
  };

  // Drag and Drop handlers
  const handleDragStart = (e, carte, produitNom) => {
    setDraggedCard({ carte, produitNom });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetCarte, targetProduitNom) => {
    e.preventDefault();
    
    if (!draggedCard) return;
    
    const { carte: sourceCarte, produitNom: sourceProduitNom } = draggedCard;
    
    // Ne rien faire si on drop sur la même carte
    if (sourceCarte.id === targetCarte.id) {
      setDraggedCard(null);
      return;
    }

    // BLOQUER le drop si les types de produits sont différents
    if (sourceProduitNom !== targetProduitNom) {
      setDraggedCard(null);
      alert('❌ Impossible de déplacer une carte vers un type de produit différent.\n\nVous ne pouvez réorganiser que les cartes du même type de produit.');
      return;
    }

    // Réorganiser les cartes (uniquement dans le même type de produit)
    const newGrouped = { ...cartesGroupees };
    const sourceList = [...newGrouped[sourceProduitNom]];

    // Retirer la carte source
    const sourceIndex = sourceList.findIndex(c => c.id === sourceCarte.id);
    const [movedCard] = sourceList.splice(sourceIndex, 1);

    // Insérer à la position cible
    const targetIndex = sourceList.findIndex(c => c.id === targetCarte.id);
    sourceList.splice(targetIndex, 0, movedCard);

    // Mettre à jour le state
    newGrouped[sourceProduitNom] = sourceList;

    setCartesGroupees(newGrouped);
    setDraggedCard(null);

    // TODO: Sauvegarder l'ordre en base de données si nécessaire
  };

  const handleChangerSemaine = async (carte, nouvelleSemaine) => {
    if (!nouvelleSemaine || nouvelleSemaine < 1 || nouvelleSemaine > 52) {
      alert('Veuillez entrer un numéro de semaine valide (1-52)');
      return;
    }

    try {
      const ancienneSemaine = carte.semaine_planifiee;
      
      const { error } = await supabase
        .from('cartes_production')
        .update({ semaine_planifiee: parseInt(nouvelleSemaine) })
        .eq('id', carte.id);

      if (error) {
        console.error('Erreur lors du changement de semaine:', error);
        alert('Erreur lors du changement de semaine');
        return;
      }

      // Recharger les cartes
      await loadCartesSemaine();
      alert(`✅ Carte déplacée vers la semaine ${nouvelleSemaine}`);
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors du changement de semaine');
    }
  };

  // Vérifier si c'est lundi entre 8h et 9h
  const verifierRappelLundi = () => {
    const maintenant = new Date();
    const jourSemaine = maintenant.getDay(); // 0=dimanche, 1=lundi
    const heure = maintenant.getHours();
    
    // Vérifier dans localStorage si déjà affiché aujourd'hui
    const dernierAffichage = localStorage.getItem('dernierRappelReport');
    const aujourdhui = new Date().toDateString();
    
    if (jourSemaine === 1 && heure === 8 && dernierAffichage !== aujourdhui) {
      // Lundi à 8h et pas encore affiché aujourd'hui
      chargerCartesEnRetard();
      setShowRappelLundi(true);
      localStorage.setItem('dernierRappelReport', aujourdhui);
    }
  };

  // Charger les cartes non terminées des semaines passées
  const chargerCartesEnRetard = async () => {
    const semaineCourante = getCurrentWeek();
    // Limiter à 8 semaines en arrière maximum pour éviter les problèmes de changement d'année
    const semaineMinimale = Math.max(1, semaineCourante - 8);
    
    console.log('🔍 Chargement cartes en retard - Semaine courante:', semaineCourante, '| Recherche depuis semaine:', semaineMinimale);
    
    const { data, error } = await supabase
      .from('cartes_production')
      .select(`
        *,
        types_produits (nom),
        suivis:suivi_operations (
          id,
          operation_id,
          validee,
          operations (ordre, nom)
        )
      `)
      .neq('statut', 'terminee')
      .gte('semaine_planifiee', semaineMinimale)
      .lt('semaine_planifiee', semaineCourante)
      .order('semaine_planifiee', { ascending: true });

    if (error) {
      console.error('Erreur chargement cartes en retard:', error);
      return;
    }

    console.log('📊 Cartes trouvées avant filtrage:', data?.length);
    data?.forEach(c => {
      const prog = calculerProgression(c);
      console.log(`  - ${c.code_barre} | S${c.semaine_planifiee} | ${c.statut} | ${prog.pourcentage}%`);
    });

    // Filtrer les cartes avec progression < 100%
    const cartesReellementEnRetard = (data || []).filter(carte => {
      const progression = calculerProgression(carte);
      return progression.pourcentage < 100;
    });

    console.log('✅ Cartes réellement en retard (< 100%):', cartesReellementEnRetard.length);

    setCartesEnRetard(cartesReellementEnRetard);
  };

  // Ouvrir la modal de report
  const handleOuvrirReportModal = async () => {
    await chargerCartesEnRetard();
    setShowReportModal(true);
  };

  // Reporter les cartes sélectionnées vers la semaine courante ou suivante
  const handleReporterCartes = async (destination) => {
    if (cartesEnRetard.length === 0) {
      alert('Aucune carte en retard à reporter.');
      return;
    }

    const semaineCible = destination === 'courante' 
      ? getCurrentWeek() 
      : getCurrentWeek() + 1;

    try {
      // Reporter toutes les cartes en retard
      const { error } = await supabase
        .from('cartes_production')
        .update({ 
          semaine_planifiee: semaineCible
        })
        .in('id', cartesEnRetard.map(c => c.id));

      if (error) {
        console.error('Erreur lors du report:', error);
        alert('Erreur lors du report des cartes');
        return;
      }

      alert(`✅ ${cartesEnRetard.length} carte(s) reportée(s) en semaine ${semaineCible}`);
      setShowReportModal(false);
      setShowRappelLundi(false);
      await loadCartesSemaine();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors du report des cartes');
    }
  };

  // ============================================
  // MODIFICATION DATE D'EXPÉDITION
  // ============================================

  const handleClickDateExpedition = (carte) => {
    if (!carte.date_expedition_max) return;
    
    setCarteEnCoursModification(carte);
    setNouvelleDate(carte.date_expedition_max.split('T')[0]); // Format YYYY-MM-DD
    setShowDateModificationModal(true);
  };

  const handleChangerDateExpedition = async () => {
    if (!nouvelleDate || !carteEnCoursModification) return;

    try {
      const carte = carteEnCoursModification;
      
      // Calculer la semaine correspondant à la nouvelle date d'expédition
      // On utilise directement la semaine de la date d'expédition
      const nouvelleSemaineCalculee = getWeekNumber(new Date(nouvelleDate));
      const semaineActuelle = carte.semaine_planifiee;

      console.log('🔍 Calcul semaine:', {
        nouvelleDate,
        nouvelleSemaineCalculee,
        semaineActuelle,
        changement: nouvelleSemaineCalculee !== semaineActuelle
      });

      // Vérifier si changement de semaine
      if (nouvelleSemaineCalculee !== semaineActuelle) {
        setNouvelleSemaine(nouvelleSemaineCalculee);
        setChangementSemaine(true);
        // Ne pas fermer la modal, attendre la confirmation
      } else {
        // Pas de changement de semaine, on met à jour directement
        await appliquerChangementDate(false);
      }
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors du calcul de la semaine');
    }
  };

  const appliquerChangementDate = async (changerSemaine) => {
    try {
      const updateData = {
        date_expedition_max: nouvelleDate
      };

      // Si on change aussi la semaine
      if (changerSemaine && nouvelleSemaine) {
        updateData.semaine_planifiee = nouvelleSemaine;
      }

      const { error } = await supabase
        .from('cartes_production')
        .update(updateData)
        .eq('id', carteEnCoursModification.id);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert('Erreur lors de la mise à jour de la date');
        return;
      }

      alert(changerSemaine && nouvelleSemaine 
        ? `✅ Date modifiée et carte déplacée en semaine ${nouvelleSemaine}`
        : '✅ Date d\'expédition mise à jour'
      );

      // Réinitialiser et recharger
      fermerModalDateModification();
      await loadCartesSemaine();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors de la mise à jour');
    }
  };

  const fermerModalDateModification = () => {
    setShowDateModificationModal(false);
    setCarteEnCoursModification(null);
    setNouvelleDate('');
    setNouvelleSemaine(null);
    setChangementSemaine(false);
  };

  // ============================================
  // MODIFICATION ET SUPPRESSION DE CARTE
  // ============================================

  // Fonction pour identifier toutes les cartes du même groupe (quantité)
  const identifierCartesGroupe = (carte) => {
    // Extraire le nom de base (sans le X/Y)
    const regex = /^(.+?)\s+\d+\/\d+$/;
    const match = carte.nom_attribue.match(regex);
    
    if (!match) {
      // Pas de pattern X/Y, c'est une carte unique
      return [carte];
    }
    
    const nomBase = match[1]; // "Nom de base"
    
    // Trouver toutes les cartes avec le même nom de base dans les cartes chargées
    const cartesGroupe = cartes.filter(c => {
      const matchAutre = c.nom_attribue.match(regex);
      return matchAutre && matchAutre[1] === nomBase;
    });
    
    return cartesGroupe.length > 0 ? cartesGroupe : [carte];
  };

  // Ouvrir la modal de modification
  const handleOuvrirModification = (carte) => {
    // Vérifier si la carte est terminée
    if (carte.statut === 'terminee') {
      alert('❌ Impossible de modifier une carte terminée.');
      return;
    }
    
    // Identifier les cartes du groupe
    const groupe = identifierCartesGroupe(carte);
    
    setCarteEnModification(carte);
    setCartesGroupeModification(groupe);
    setFormModification({
      nom_attribue: carte.nom_attribue.replace(/\s+\d+\/\d+$/, ''), // Enlever le X/Y
      date_creation: carte.date_creation ? carte.date_creation.split('T')[0] : ''
    });
    setShowModalModification(true);
  };

  // Sauvegarder les modifications
  const handleSauvegarderModification = async () => {
    const confirmation = window.confirm(
      cartesGroupeModification.length > 1
        ? `⚠️ Cette modification sera appliquée à ${cartesGroupeModification.length} cartes du même groupe.\n\nConfirmer ?`
        : '⚠️ Confirmer la modification de cette carte ?'
    );
    
    if (!confirmation) return;
    
    try {
      // Pour chaque carte du groupe
      for (let i = 0; i < cartesGroupeModification.length; i++) {
        const carte = cartesGroupeModification[i];
        
        // Reconstituer le nom avec le pattern X/Y si plusieurs cartes
        let nouveauNom = formModification.nom_attribue;
        if (cartesGroupeModification.length > 1) {
          nouveauNom = `${formModification.nom_attribue} ${i + 1}/${cartesGroupeModification.length}`;
        }
        
        const { error } = await supabase
          .from('cartes_production')
          .update({
            nom_attribue: nouveauNom,
            date_creation: formModification.date_creation,
            updated_at: new Date().toISOString()
          })
          .eq('id', carte.id);
        
        if (error) throw error;
      }
      
      alert(`✅ ${cartesGroupeModification.length} carte(s) modifiée(s) avec succès !`);
      setShowModalModification(false);
      loadCartesSemaine(); // Recharger les données
      
    } catch (error) {
      console.error('Erreur modification:', error);
      alert('❌ Erreur lors de la modification');
    }
  };

  // Supprimer les cartes
  const handleSupprimerCarte = () => {
    // Si carte unique (pas de groupe), supprimer directement avec confirmation
    if (cartesGroupeModification.length <= 1) {
      executerSuppression('seule');
      return;
    }
    // Si groupe, afficher la modale de choix
    setShowModalSuppression(true);
  };

  // Exécuter la suppression selon le mode choisi
  const executerSuppression = async (mode) => {
    // mode = 'seule' → supprimer uniquement carteEnModification
    // mode = 'groupe' → supprimer tout cartesGroupeModification

    const cartesASupprimer = mode === 'seule' 
      ? [carteEnModification] 
      : cartesGroupeModification;

    const label = mode === 'seule'
      ? `🗑️ Supprimer la carte "${carteEnModification.nom_attribue}" ?\n\n`
      : `🗑️ Supprimer les ${cartesGroupeModification.length} cartes du groupe ?\n\n`;

    const confirmation = window.confirm(
      label +
      `• Opérations validées\n` +
      `• Commentaires\n` +
      `• Historique de progression\n\n` +
      `Cette action est IRRÉVERSIBLE.\n\nConfirmer la suppression ?`
    );
    
    if (!confirmation) return;
    
    // Double confirmation si des cartes ont une progression
    const hasProgress = cartesASupprimer.some(c => 
      c.statut === 'en_cours' && calculerProgression(c).pourcentage > 0
    );
    
    if (hasProgress) {
      const secondConfirmation = window.confirm(
        '⚠️ DERNIÈRE CONFIRMATION :\n\n' +
        'Des opérations ont déjà été validées sur cette/ces carte(s).\n' +
        'Êtes-vous VRAIMENT sûr de vouloir supprimer ?'
      );
      
      if (!secondConfirmation) return;
    }
    
    try {
      for (const carte of cartesASupprimer) {
        const { error } = await supabase
          .from('cartes_production')
          .delete()
          .eq('id', carte.id);
        
        if (error) throw error;
      }
      
      alert(`✅ ${cartesASupprimer.length} carte(s) supprimée(s) avec succès !`);
      setShowModalSuppression(false);
      setShowModalModification(false);
      loadCartesSemaine();
      
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  // ============================================
  // COMMENTAIRES
  // ============================================

  const handleOuvrirCommentaires = (carte) => {
    // Récupérer l'opérateur connecté depuis localStorage (si PosteScan)
    // Ou utiliser un opérateur par défaut pour l'admin
    const operateurLocal = localStorage.getItem('operateur');
    if (operateurLocal) {
      setOperateurConnecte(JSON.parse(operateurLocal));
    } else {
      // Pour l'admin, créer un opérateur fictif
      setOperateurConnecte({
        id: '00000000-0000-0000-0000-000000000000',
        nom: 'Administrateur'
      });
    }
    
    setCarteSelectionneeCommentaires(carte);
    setShowCommentairesPanel(true);
  };

  const handleFermerCommentaires = () => {
    setShowCommentairesPanel(false);
    setCarteSelectionneeCommentaires(null);
  };

  // Toggle statut graphisme (À faire / Prêt)
  const handleClickGraphisme = (carte) => {
    const groupe = identifierCartesGroupe(carte);
    
    // Si carte unique (pas de groupe), basculer directement
    if (groupe.length <= 1) {
      executerToggleGraphisme(carte, [carte]);
      return;
    }
    
    // Si groupe, afficher la modal de choix
    setCarteGraphisme(carte);
    setCartesGroupeGraphisme(groupe);
    setShowModalGraphisme(true);
  };

  const executerToggleGraphisme = async (carte, cartesAToggle) => {
    const nouveauStatut = !carte.graphisme_pret;
    
    try {
      for (const c of cartesAToggle) {
        const { error } = await supabase
          .from('cartes_production')
          .update({ graphisme_pret: nouveauStatut })
          .eq('id', c.id);

        if (error) {
          console.error('Erreur toggle graphisme:', error);
          alert('Erreur lors de la mise à jour du statut graphisme');
          return;
        }
      }

      setShowModalGraphisme(false);
      await loadCartesSemaine();
    } catch (err) {
      console.error('Erreur toggle graphisme:', err);
      alert('Erreur lors de la mise à jour du statut graphisme');
    }
  };

  const handleImprimerEtiquette = (carte) => {
    // Fonction 100% identique à la V1
    const canvas = document.createElement('canvas');
    
    try {
      JsBarcode(canvas, carte.code_barre, {
        format: 'CODE128',
        width: 3,
        height: 70,
        displayValue: false,
        margin: 0
      });
      
      const barcodeDataUrl = canvas.toDataURL('image/png');
      
      const printWindow = window.open('', '', 'width=400,height=200');
      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression - ${carte.nom_attribue || carte.code_barre}</title>
            <style>
              @page {
                size: 89mm 36mm;
                margin: 0;
              }
              
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                width: 89mm;
                height: 36mm;
                margin: 0;
                padding: 2mm 2mm;
                overflow: hidden;
              }
              
              .label-container {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              }
              
              .barcode-section {
                text-align: center;
                margin-bottom: 0.5mm;
              }
              
              .barcode-image {
                width: 70mm;
                height: auto;
                max-height: 14mm;
                margin: 0 auto 0.5mm auto;
                display: block;
              }
              
              .code-text {
                font-family: 'Courier New', monospace;
                font-size: 8pt;
                font-weight: bold;
                color: #000;
                letter-spacing: 0.5px;
              }
              
              .product-info {
                text-align: center;
                margin-top: 1mm;
              }
              
              .product-name {
                font-size: 10pt;
                font-weight: bold;
                color: #000;
                margin-bottom: 0.5mm;
              }
              
              .product-type {
                font-size: 8pt;
                color: #333;
                font-weight: 600;
              }
            </style>
          </head>
          <body>
            <div class="label-container">
              <div class="barcode-section">
                <img src="${barcodeDataUrl}" class="barcode-image" alt="Code-barre" />
                <div class="code-text">${carte.code_barre}</div>
              </div>
              
              <div class="product-info">
                <div class="product-name">${carte.nom_attribue || 'Carte de production'}</div>
                <div class="product-type">${carte.types_produits?.nom || ''}</div>
              </div>
            </div>
          </body>
        </html>
      `;
      
      printWindow.document.write(content);
      printWindow.document.close();
      
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
    } catch (error) {
      console.error('Erreur lors de la génération du code-barre:', error);
      alert('Erreur lors de la génération du code-barre');
    }
  };

  const cartesFiltrées = {};
  Object.entries(cartesGroupees).forEach(([produitNom, cartesListe]) => {
    const filtered = filtrerCartes(cartesListe);
    if (filtered.length > 0) {
      cartesFiltrées[produitNom] = filtered;
    }
  });

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* En-tête */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#212529',
            margin: '0 0 8px 0'
          }}>
            📊 Planning Production
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#6c757d',
            margin: 0
          }}>
            Vue Tableau • Nouvelle version
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => exporterPlanningPDF(cartes)}
            style={{
              padding: '10px 16px',
              backgroundColor: 'white',
              border: '2px solid #e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            📄 PDF
          </button>
          <button
            onClick={() => exporterPlanningExcel(cartes)}
            style={{
              padding: '10px 16px',
              backgroundColor: 'white',
              border: '2px solid #e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            📊 Excel
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        position: 'relative'
      }}>
        {/* Barre de recherche */}
        <div style={{ marginBottom: '16px', position: 'relative' }}>
          <input
            type="text"
            placeholder="🔍 Rechercher dans tout le planning (nom, code-barre, produit)..."
            value={searchTerm}
            onChange={(e) => handleSearchGlobal(e.target.value)}
            onFocus={() => searchTerm.trim() !== '' && setShowSearchResults(true)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '2px solid #e0e5ea',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.borderColor = '#1aabf9'}
            onMouseLeave={(e) => !e.target.matches(':focus') && (e.target.style.borderColor = '#e0e5ea')}
          />
          
          {/* Résultats de recherche */}
          {showSearchResults && searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '8px',
              backgroundColor: 'white',
              border: '2px solid #1aabf9',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 1000
            }}>
              <div style={{ 
                padding: '12px 16px', 
                borderBottom: '1px solid #e0e5ea',
                fontSize: '13px',
                fontWeight: '600',
                color: '#6c757d',
                backgroundColor: '#f8f9fa'
              }}>
                {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}
              </div>
              {searchResults.map((carte) => {
                const badge = getBadgeCouleur(carte);
                return (
                  <div
                    key={carte.id}
                    onClick={() => handleSelectSearchResult(carte)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: badge.bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px'
                      }}>
                        {badge.emoji}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#212529',
                          marginBottom: '2px'
                        }}>
                          {carte.nom_attribue}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#6c757d'
                        }}>
                          {carte.types_produits?.nom} • {carte.code_barre}
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 12px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#1aabf9'
                      }}>
                        📅 Semaine {carte.semaine_planifiee}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div 
                onClick={() => setShowSearchResults(false)}
                style={{
                  padding: '10px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: '#6c757d',
                  cursor: 'pointer',
                  borderTop: '1px solid #e0e5ea',
                  backgroundColor: '#f8f9fa'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              >
                Fermer
              </div>
            </div>
          )}
        </div>

        {/* Filtres par statut et bouton Nouvelle Production */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#495057' }}>
              Filtres:
            </span>
          <button
            onClick={() => setFiltreStatut('tous')}
            style={{
              padding: '8px 16px',
              backgroundColor: filtreStatut === 'tous' ? '#1aabf9' : 'white',
              color: filtreStatut === 'tous' ? 'white' : '#495057',
              border: '2px solid',
              borderColor: filtreStatut === 'tous' ? '#1aabf9' : '#e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            Tout ({compterCartesParStatut('tous')})
          </button>
          <button
            onClick={() => setFiltreStatut('en_retard')}
            style={{
              padding: '8px 16px',
              backgroundColor: filtreStatut === 'en_retard' ? '#ef4444' : 'white',
              color: filtreStatut === 'en_retard' ? 'white' : '#495057',
              border: '2px solid',
              borderColor: filtreStatut === 'en_retard' ? '#ef4444' : '#e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            🔴 Retard ({compterCartesParStatut('en_retard')})
          </button>
          <button
            onClick={() => setFiltreStatut('urgent')}
            style={{
              padding: '8px 16px',
              backgroundColor: filtreStatut === 'urgent' ? '#f59e0b' : 'white',
              color: filtreStatut === 'urgent' ? 'white' : '#495057',
              border: '2px solid',
              borderColor: filtreStatut === 'urgent' ? '#f59e0b' : '#e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            🟡 Urgent ({compterCartesParStatut('urgent')})
          </button>
          <button
            onClick={() => setFiltreStatut('en_cours')}
            style={{
              padding: '8px 16px',
              backgroundColor: filtreStatut === 'en_cours' ? '#6b7280' : 'white',
              color: filtreStatut === 'en_cours' ? 'white' : '#495057',
              border: '2px solid',
              borderColor: filtreStatut === 'en_cours' ? '#6b7280' : '#e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            ⚪ En cours ({compterCartesParStatut('en_cours')})
          </button>
          <button
            onClick={() => setFiltreStatut('termine')}
            style={{
              padding: '8px 16px',
              backgroundColor: filtreStatut === 'termine' ? '#10b981' : 'white',
              color: filtreStatut === 'termine' ? 'white' : '#495057',
              border: '2px solid',
              borderColor: filtreStatut === 'termine' ? '#10b981' : '#e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            🟢 OK ({compterCartesParStatut('termine')})
          </button>
          <button
            onClick={() => setFiltreStatut('graphisme_a_faire')}
            style={{
              padding: '8px 16px',
              backgroundColor: filtreStatut === 'graphisme_a_faire' ? '#dc3545' : 'white',
              color: filtreStatut === 'graphisme_a_faire' ? 'white' : '#495057',
              border: '2px solid',
              borderColor: filtreStatut === 'graphisme_a_faire' ? '#dc3545' : '#e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            🎨 Graphisme à faire ({compterCartesParStatut('graphisme_a_faire')})
          </button>
          <button
            onClick={() => setFiltreStatut('production_non_commencee')}
            style={{
              padding: '8px 16px',
              backgroundColor: filtreStatut === 'production_non_commencee' ? '#6c757d' : 'white',
              color: filtreStatut === 'production_non_commencee' ? 'white' : '#495057',
              border: '2px solid',
              borderColor: filtreStatut === 'production_non_commencee' ? '#6c757d' : '#e0e5ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            ⏸️ Production non commencée ({compterCartesParStatut('production_non_commencee')})
          </button>
          </div>

          {/* Boutons d'action */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleOuvrirReportModal}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
            >
              📅 Reporter cartes en retard
            </button>
            <button
              onClick={openModal}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
            >
              ➕ Nouvelle Production
            </button>
            <button
              onClick={openValidationModal}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1aabf9',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1590d6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1aabf9'}
            >
              👌🏻 Valider une opération
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Semaine - POSITION FIXE */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: '0',
        zIndex: 100
      }}>
        <button
          onClick={() => handleChangeSemaine(-1)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#f8f9fa',
            border: '2px solid #e0e5ea',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ⬅️ Semaine {semaineCourante - 1 < 1 ? 52 : semaineCourante - 1}
        </button>

        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#212529',
            margin: '0 0 4px 0'
          }}>
            📅 SEMAINE {semaineCourante}
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#6c757d',
            margin: '0 0 8px 0'
          }}>
            {getWeekDates(semaineCourante)} • {statistiques.nombreCartes} cartes
          </p>
          {/* Bandeau Charge vs Capacité */}
          {(() => {
            const chargeSecondes = statistiques.totalHeures;
            const chargeMinutes = Math.round(chargeSecondes / 60);
            const capaciteMinutes = capaciteHebdo;
            const pourcentageCharge = capaciteMinutes > 0 ? Math.round((chargeMinutes / capaciteMinutes) * 100) : 0;
            const enSurcharge = chargeMinutes > capaciteMinutes && capaciteMinutes > 0;

            return (
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: enSurcharge ? '#fff3cd' : '#e8f5e9',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  border: `1px solid ${enSurcharge ? '#ffc107' : '#c8e6c9'}`,
                  fontSize: '13px'
                }}>
                  <span style={{ fontWeight: '600', color: enSurcharge ? '#856404' : '#2e7d32' }}>
                    ⚡ Charge : {formatDuree(chargeSecondes)}
                  </span>
                  <span style={{ color: '#6c757d' }}>/</span>
                  <span style={{ fontWeight: '600', color: '#1565c0' }}>
                    🏭 Capacité : {formatHeuresMinutes(capaciteMinutes)}
                  </span>
                  <span style={{
                    backgroundColor: enSurcharge ? '#dc3545' : pourcentageCharge > 80 ? '#ff9800' : '#28a745',
                    color: '#ffffff',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '700',
                    marginLeft: '4px'
                  }}>
                    {pourcentageCharge}%
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        <button
          onClick={() => handleChangeSemaine(1)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#f8f9fa',
            border: '2px solid #e0e5ea',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          Semaine {semaineCourante + 1 > 52 ? 1 : semaineCourante + 1} ➡️
        </button>
      </div>

      {/* Groupes de produits */}
      {Object.keys(cartesFiltrées).length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📅</div>
          <h3 style={{
            color: '#212529',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            {filtreStatut === 'tous' ? 'Aucune production prévue' : 'Aucune carte avec ce statut'}
          </h3>
          <p style={{ color: '#6c757d', fontSize: '14px' }}>
            {filtreStatut === 'tous' 
              ? 'Les cartes sont automatiquement planifiées selon leur délai d\'expédition'
              : 'Changez de filtre pour voir d\'autres cartes'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {Object.entries(cartesFiltrées).map(([produitNom, cartesListe]) => {
            const isDeplie = groupesDepliés[produitNom];
            const tempsGroupe = calculerSecondesGroupe(cartesListe);

            return (
              <div
                key={produitNom}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }}
              >
                {/* En-tête groupe produit */}
                <div
                  onClick={() => toggleGroupe(produitNom)}
                  style={{
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderBottom: isDeplie ? '2px solid #e0e5ea' : 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '20px' }}>
                      {isDeplie ? '▼' : '►'}
                    </span>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#212529',
                      margin: 0
                    }}>
                      🔧 {produitNom}
                    </h3>
                    <span style={{
                      fontSize: '14px',
                      color: '#6c757d',
                      fontWeight: '600'
                    }}>
                      ({cartesListe.length} {cartesListe.length > 1 ? 'cartes' : 'carte'} • {formatDuree(tempsGroupe)})
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openValidationGroupeeModal(produitNom, cartesListe);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                    >
                      🔄 Validation groupée
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroupe(produitNom);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        border: '1px solid #e0e5ea',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6c757d'
                      }}
                    >
                      {isDeplie ? 'Replier' : 'Déplier'}
                    </button>
                  </div>
                </div>

                {/* Tableau des cartes */}
                {isDeplie && (
                  <div style={{ 
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      minWidth: '800px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '80px'
                          }}>
                            Statut
                          </th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '250px'
                          }}>
                            Nom
                          </th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '100px'
                          }}>
                            Graphisme
                          </th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '130px'
                          }}>
                            Date Création
                          </th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '150px'
                          }}>
                            Date Expédition
                          </th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '120px'
                          }}>
                            Progression
                          </th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            minWidth: '250px'
                          }}>
                            Opération en cours
                          </th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '200px'
                          }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartesListe.map((carte) => {
                          const badge = getBadgeCouleur(carte);
                          const progression = calculerProgression(carte);
                          const etapeEnCours = getEtapeEnCours(carte);

                          return (
                            <tr
                              key={carte.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, carte, produitNom)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, carte, produitNom)}
                              style={{
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: highlightedCarteId === carte.id 
                                  ? '#fff3cd' 
                                  : draggedCard?.carte.id === carte.id 
                                    ? '#e3f2fd' 
                                    : 'white',
                                cursor: 'grab',
                                transition: 'background-color 0.3s ease',
                                boxShadow: highlightedCarteId === carte.id ? '0 0 0 2px #ffc107' : 'none'
                              }}
                              onMouseEnter={(e) => {
                                if (draggedCard?.carte.id !== carte.id && highlightedCarteId !== carte.id) {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (draggedCard?.carte.id !== carte.id && highlightedCarteId !== carte.id) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              {/* Colonne Statut */}
                              <td style={{
                                padding: '16px',
                                textAlign: 'center'
                              }}>
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: badge.bgColor,
                                  fontSize: '20px'
                                }}>
                                  {badge.emoji}
                                </div>
                              </td>

                              {/* Colonne Nom */}
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#212529'
                              }}>
                                {carte.nom_attribue}
                              </td>

                              {/* Colonne Graphisme */}
                              <td style={{
                                padding: '16px',
                                textAlign: 'center'
                              }}>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickGraphisme(carte);
                                  }}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '5px 12px',
                                    borderRadius: '20px',
                                    backgroundColor: carte.graphisme_pret ? '#d4edda' : '#fff3cd',
                                    color: carte.graphisme_pret ? '#28a745' : '#dc3545',
                                    border: `1px solid ${carte.graphisme_pret ? '#28a745' : '#ffc107'}`,
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    userSelect: 'none'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.08)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                  title={carte.graphisme_pret ? 'Cliquer pour repasser à "À faire"' : 'Cliquer pour marquer comme "Prêt"'}
                                >
                                  {carte.graphisme_pret ? '🟢 Prêt' : '🔴 À faire'}
                                </div>
                              </td>

                              {/* Colonne Date Création */}
                              <td style={{
                                padding: '16px',
                                textAlign: 'center',
                                fontSize: '13px',
                                color: '#6c757d'
                              }}>
                                {new Date(carte.date_creation).toLocaleDateString('fr-FR')}
                              </td>

                              {/* Colonne Date Expédition */}
                              <td style={{
                                padding: '16px',
                                textAlign: 'center',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}>
                                {carte.date_expedition_max ? (
                                  <div 
                                    onClick={() => handleClickDateExpedition(carte)}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      backgroundColor: badge.bgColor,
                                      color: badge.color,
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s, box-shadow 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'scale(1)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    title="Cliquer pour modifier la date d'expédition"
                                  >
                                    {badge.emoji} {new Date(carte.date_expedition_max).toLocaleDateString('fr-FR')}
                                  </div>
                                ) : (
                                  <span style={{ color: '#adb5bd' }}>-</span>
                                )}
                              </td>

                              {/* Colonne Progression - Cliquable */}
                              <td style={{ 
                                padding: '16px',
                                textAlign: 'center'
                              }}>
                                <div 
                                  onClick={() => handleOpenProgressModal(carte)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    cursor: 'pointer'
                                  }}
                                  title="Cliquer pour voir le détail des opérations"
                                >
                                  <div style={{
                                    width: '80px',
                                    height: '24px',
                                    backgroundColor: '#e9ecef',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    transition: 'transform 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                  >
                                    <div style={{
                                      width: `${progression.pourcentage}%`,
                                      height: '100%',
                                      backgroundColor: badge.color,
                                      transition: 'width 0.3s ease',
                                      borderRadius: '12px'
                                    }} />
                                  </div>
                                  <span style={{
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: '#212529',
                                    minWidth: '45px',
                                    transition: 'color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = '#1aabf9'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = '#212529'}
                                  >
                                    {progression.pourcentage}%
                                  </span>
                                </div>
                              </td>

                              {/* Colonne Opération en cours */}
                              <td style={{
                                padding: '16px',
                                fontSize: '14px',
                                color: '#6c757d',
                                fontStyle: 'italic'
                              }}>
                                {etapeEnCours}
                              </td>

                              {/* Colonne Actions */}
                              <td style={{
                                padding: '16px',
                                textAlign: 'center'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '8px'
                                }}>
                                  <button
                                    onClick={() => handleImprimerEtiquette(carte)}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: '#1aabf9',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '6px',
                                      transition: 'background-color 0.2s',
                                      height: '36px',
                                      minWidth: '40px'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1590d6'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1aabf9'}
                                  >
                                    🖨️ Imprimer
                                  </button>
                                  <button
                                    onClick={() => {
                                      const nouvelleSemaine = prompt('Changer la semaine planifiée (1-52):', carte.semaine_planifiee || '');
                                      if (nouvelleSemaine) {
                                        handleChangerSemaine(carte, parseInt(nouvelleSemaine));
                                      }
                                    }}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '6px',
                                      transition: 'background-color 0.2s',
                                      height: '36px',
                                      minWidth: '40px'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                                    title="Changer la semaine planifiée"
                                  >
                                    📅 S{carte.semaine_planifiee || '?'}
                                  </button>
                                  <button
                                    onClick={() => handleOuvrirCommentaires(carte)}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: '#8b5cf6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '6px',
                                      transition: 'background-color 0.2s',
                                      height: '36px',
                                      minWidth: '40px'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                                    title="Voir les commentaires"
                                  >
                                    💬
                                  </button>
                                  <button
                                    onClick={() => handleOuvrirModification(carte)}
                                    disabled={carte.statut === 'terminee'}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: carte.statut === 'terminee' ? '#adb5bd' : '#ffc107',
                                      color: carte.statut === 'terminee' ? '#6c757d' : '#000',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: carte.statut === 'terminee' ? 'not-allowed' : 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '6px',
                                      transition: 'background-color 0.2s',
                                      opacity: carte.statut === 'terminee' ? 0.5 : 1,
                                      height: '36px',
                                      minWidth: '40px'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (carte.statut !== 'terminee') {
                                        e.currentTarget.style.backgroundColor = '#e0a800';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (carte.statut !== 'terminee') {
                                        e.currentTarget.style.backgroundColor = '#ffc107';
                                      }
                                    }}
                                    title={carte.statut === 'terminee' 
                                      ? 'Impossible de modifier une carte terminée' 
                                      : 'Modifier la carte'}
                                  >
                                    ✏️ Modifier
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Nouvelle Production */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            padding: '40px',
            width: '550px',
            maxWidth: '90%',
            border: '2px solid #1aabf9',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ color: '#212529', marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>
              Nouvelle Production
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
                  Type de produit *
                </label>
                <select
                  value={formData.type_produit_id}
                  onChange={(e) => setFormData({...formData, type_produit_id: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Sélectionner un produit</option>
                  {typesProduits.map(tp => (
                    <option key={tp.id} value={tp.id}>{tp.nom}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
                  Nom de la carte *
                </label>
                <input
                  type="text"
                  value={formData.nom_attribue}
                  onChange={(e) => setFormData({...formData, nom_attribue: e.target.value})}
                  required
                  placeholder="Ex: Chaise Client Dupont"
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
                  Quantité *
                </label>
                <input
                  type="number"
                  value={formData.quantite}
                  onChange={(e) => setFormData({...formData, quantite: Math.max(1, Math.min(100, parseInt(e.target.value) || 1))})}
                  required
                  min="1"
                  max="100"
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px'
                  }}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Nombre de cartes à créer (1 à 100). Si supérieur à 1, une numérotation sera ajoutée au nom.
                </small>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
                  Date de création *
                </label>
                <input
                  type="date"
                  value={formData.date_creation}
                  onChange={(e) => setFormData({...formData, date_creation: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px',
                    cursor: 'pointer'
                  }}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  La date d'expédition sera calculée automatiquement à partir de cette date
                </small>
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #1aabf9',
                borderRadius: '10px',
                marginBottom: '25px',
                fontSize: '13px',
                color: '#0d96e6'
              }}>
                🔖 <strong>Code-barre automatique :</strong> Un code-barre unique sera généré automatiquement pour chaque carte.
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    padding: '12px 28px',
                    backgroundColor: 'transparent',
                    color: '#6c757d',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 28px',
                    backgroundColor: '#1aabf9',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Créer la carte
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Modification Carte */}
      {showModalModification && carteEnModification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            padding: '40px',
            width: '550px',
            maxWidth: '90%',
            border: '2px solid #ffc107',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ 
              color: '#212529', 
              marginTop: 0, 
              marginBottom: '10px', 
              fontSize: '24px', 
              fontWeight: '600' 
            }}>
              ✏️ Modifier la carte
            </h2>
            
            {cartesGroupeModification.length > 1 && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                fontSize: '14px',
                color: '#856404'
              }}>
                ⚠️ Cette modification sera appliquée à <strong>{cartesGroupeModification.length} cartes</strong> du même groupe
              </div>
            )}
            
            <form onSubmit={(e) => { e.preventDefault(); handleSauvegarderModification(); }}>
              {/* Champ Nom */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  color: '#495057', 
                  marginBottom: '10px', 
                  fontSize: '14px', 
                  fontWeight: '600' 
                }}>
                  Nom de la carte *
                </label>
                <input
                  type="text"
                  value={formModification.nom_attribue}
                  onChange={(e) => setFormModification({
                    ...formModification, 
                    nom_attribue: e.target.value
                  })}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px'
                  }}
                />
                {cartesGroupeModification.length > 1 && (
                  <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '6px', display: 'block' }}>
                    Les numéros (1/{cartesGroupeModification.length}, 2/{cartesGroupeModification.length}, etc.) seront automatiquement ajoutés
                  </small>
                )}
              </div>

              {/* Champ Date de création */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  color: '#495057', 
                  marginBottom: '10px', 
                  fontSize: '14px', 
                  fontWeight: '600' 
                }}>
                  Date de création *
                </label>
                <input
                  type="date"
                  value={formModification.date_creation}
                  onChange={(e) => setFormModification({
                    ...formModification, 
                    date_creation: e.target.value
                  })}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px'
                  }}
                />
              </div>

              {/* Informations non modifiables */}
              <div style={{
                backgroundColor: '#e9ecef',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#495057', 
                  marginTop: 0, 
                  marginBottom: '12px' 
                }}>
                  Informations non modifiables
                </h4>
                <div style={{ fontSize: '13px', color: '#6c757d' }}>
                  <div style={{ marginBottom: '6px' }}><strong>Type de produit :</strong> {carteEnModification.types_produits?.nom || 'N/A'}</div>
                  <div style={{ marginBottom: '6px' }}><strong>Code-barre :</strong> {carteEnModification.code_barre}</div>
                  <div style={{ marginBottom: '6px' }}><strong>Date d'expédition :</strong> {carteEnModification.date_expedition_max ? new Date(carteEnModification.date_expedition_max).toLocaleDateString('fr-FR') : 'Non définie'}</div>
                  <div><strong>Statut :</strong> {carteEnModification.statut}</div>
                </div>
              </div>

              {/* Boutons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'space-between',
                marginTop: '30px' 
              }}>
                {/* Bouton Supprimer à gauche */}
                <button
                  type="button"
                  onClick={handleSupprimerCarte}
                  style={{
                    padding: '14px 24px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                >
                  🗑️ Supprimer
                </button>

                {/* Boutons Annuler et Enregistrer à droite */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setShowModalModification(false)}
                    style={{
                      padding: '14px 24px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                  >
                    Annuler
                  </button>
                  
                  <button
                    type="submit"
                    style={{
                      padding: '14px 24px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                  >
                    ✅ Enregistrer
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Choix de suppression (carte seule vs groupe) */}
      {showModalSuppression && carteEnModification && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '32px',
            width: '480px',
            maxWidth: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '20px', 
              color: '#dc3545',
              textAlign: 'center'
            }}>
              🗑️ Suppression de carte
            </h3>
            
            <p style={{ 
              textAlign: 'center', 
              color: '#6c757d', 
              fontSize: '14px',
              margin: '0 0 24px 0'
            }}>
              Cette carte fait partie d'un groupe de <strong>{cartesGroupeModification.length} cartes</strong>.
              <br />Que souhaitez-vous supprimer ?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Bouton supprimer carte seule */}
              <button
                onClick={() => executerSuppression('seule')}
                style={{
                  padding: '16px 20px',
                  backgroundColor: '#fff',
                  color: '#dc3545',
                  border: '2px solid #dc3545',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc3545';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = '#dc3545';
                }}
              >
                🗑️ Supprimer uniquement cette carte
                <div style={{ fontSize: '12px', fontWeight: '400', marginTop: '4px', opacity: 0.8 }}>
                  "{carteEnModification.nom_attribue}"
                </div>
              </button>

              {/* Bouton supprimer tout le groupe */}
              <button
                onClick={() => executerSuppression('groupe')}
                style={{
                  padding: '16px 20px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: '2px solid #dc3545',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
              >
                🗑️ Supprimer les {cartesGroupeModification.length} cartes du groupe
                <div style={{ fontSize: '12px', fontWeight: '400', marginTop: '4px', opacity: 0.8 }}>
                  {cartesGroupeModification.map(c => c.nom_attribue).join(', ')}
                </div>
              </button>

              {/* Bouton annuler */}
              <button
                onClick={() => setShowModalSuppression(false)}
                style={{
                  padding: '14px 20px',
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'center',
                  marginTop: '4px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Choix graphisme (carte seule vs groupe) */}
      {showModalGraphisme && carteGraphisme && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '32px',
            width: '480px',
            maxWidth: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '20px', 
              color: carteGraphisme.graphisme_pret ? '#dc3545' : '#28a745',
              textAlign: 'center'
            }}>
              🎨 {carteGraphisme.graphisme_pret ? 'Repasser en "À faire"' : 'Marquer comme "Prêt"'} ?
            </h3>
            
            <p style={{ 
              textAlign: 'center', 
              color: '#6c757d', 
              fontSize: '14px',
              margin: '0 0 24px 0'
            }}>
              Cette carte fait partie d'un groupe de <strong>{cartesGroupeGraphisme.length} cartes</strong>.
              <br />Que souhaitez-vous modifier ?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Bouton carte seule */}
              <button
                onClick={() => executerToggleGraphisme(carteGraphisme, [carteGraphisme])}
                style={{
                  padding: '16px 20px',
                  backgroundColor: '#fff',
                  color: carteGraphisme.graphisme_pret ? '#dc3545' : '#28a745',
                  border: `2px solid ${carteGraphisme.graphisme_pret ? '#dc3545' : '#28a745'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = carteGraphisme.graphisme_pret ? '#dc3545' : '#28a745';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = carteGraphisme.graphisme_pret ? '#dc3545' : '#28a745';
                }}
              >
                🎨 Cette carte uniquement
                <div style={{ fontSize: '12px', fontWeight: '400', marginTop: '4px', opacity: 0.8 }}>
                  "{carteGraphisme.nom_attribue}"
                </div>
              </button>

              {/* Bouton tout le groupe */}
              <button
                onClick={() => executerToggleGraphisme(carteGraphisme, cartesGroupeGraphisme)}
                style={{
                  padding: '16px 20px',
                  backgroundColor: carteGraphisme.graphisme_pret ? '#dc3545' : '#28a745',
                  color: '#fff',
                  border: `2px solid ${carteGraphisme.graphisme_pret ? '#dc3545' : '#28a745'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                🎨 Toutes les cartes du groupe ({cartesGroupeGraphisme.length})
              </button>

              {/* Bouton annuler */}
              <button
                onClick={() => setShowModalGraphisme(false)}
                style={{
                  padding: '14px 20px',
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  textAlign: 'center',
                  marginTop: '4px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Validation d'opération */}
      {showValidationModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            backgroundColor: '#ffffff', 
            borderRadius: '20px', 
            padding: '40px', 
            width: '600px', 
            maxWidth: '90%', 
            maxHeight: '90vh', 
            overflow: 'auto', 
            border: '2px solid #28a745' 
          }}>
            <h2 style={{ color: '#212529', marginTop: 0, marginBottom: '10px', fontSize: '24px', fontWeight: '600' }}>
              Valider une opération
            </h2>
            <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '30px' }}>
              Étape {validationStep} sur 3
            </div>

            {/* Étape 1 : Identification opérateur */}
            {validationStep === 1 && (
              <div>
                <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '1px solid #dee2e6' }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#212529', marginBottom: '15px' }}>
                    🔍 Étape 1 : Identification opérateur
                  </div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                    Scannez ou saisissez le code-barre opérateur
                  </label>
                  
                  {!showScanner && (
                    <>
                      <input
                        type="text"
                        value={validationData.operateurCode}
                        onChange={(e) => setValidationData({ ...validationData, operateurCode: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && handleScanOperateur()}
                        placeholder="OPR-XXXXXXXX"
                        autoFocus
                        style={{ width: '100%', padding: '12px', backgroundColor: '#ffffff', border: '2px solid #28a745', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', marginBottom: '10px' }}
                      />
                      <button
                        type="button"
                        onClick={() => startScanner('operateur')}
                        style={{ width: '100%', padding: '12px', backgroundColor: '#007bff', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}
                      >
                        📷 Scanner avec la caméra
                      </button>
                    </>
                  )}
                  
                  {showScanner && scannerType === 'operateur' && (
                    <>
                      <div id="scanner-operateur" style={{ marginBottom: '10px' }}></div>
                      <button
                        type="button"
                        onClick={stopScanner}
                        style={{ width: '100%', padding: '12px', backgroundColor: '#dc3545', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                      >
                        ✕ Annuler le scan
                      </button>
                    </>
                  )}
                  
                  {!showScanner && (
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px' }}>
                      💡 Appuyez sur Entrée après la saisie manuelle
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={closeValidationModal}
                    style={{ padding: '12px 28px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleScanOperateur}
                    disabled={showScanner}
                    style={{ padding: '12px 28px', backgroundColor: showScanner ? '#dee2e6' : '#28a745', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: showScanner ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            )}

            {/* Étape 2 : Scan carte production */}
            {validationStep === 2 && (
              <div>
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#d4edda', borderRadius: '10px', border: '1px solid #28a745' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#155724' }}>
                    ✓ Opérateur identifié : {validationData.operateur.prenom} {validationData.operateur.nom}
                  </div>
                  {validationData.operateur.profils && (
                    <div style={{ fontSize: '12px', color: '#155724', marginTop: '5px' }}>
                      Profil : {validationData.operateur.profils.nom}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '1px solid #dee2e6' }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#212529', marginBottom: '15px' }}>
                    📦 Étape 2 : Scan carte production
                  </div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                    Scannez ou saisissez le code-barre de la carte
                  </label>
                  
                  {!showScanner && (
                    <>
                      <input
                        type="text"
                        value={validationData.carteCode}
                        onChange={(e) => setValidationData({ ...validationData, carteCode: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && handleScanCarte()}
                        placeholder="PROD-XXXXXXXX"
                        autoFocus
                        style={{ width: '100%', padding: '12px', backgroundColor: '#ffffff', border: '2px solid #28a745', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', marginBottom: '10px' }}
                      />
                      <button
                        type="button"
                        onClick={() => startScanner('carte')}
                        style={{ width: '100%', padding: '12px', backgroundColor: '#007bff', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}
                      >
                        📷 Scanner avec la caméra
                      </button>
                    </>
                  )}
                  
                  {showScanner && scannerType === 'carte' && (
                    <>
                      <div id="scanner-carte" style={{ marginBottom: '10px' }}></div>
                      <button
                        type="button"
                        onClick={stopScanner}
                        style={{ width: '100%', padding: '12px', backgroundColor: '#dc3545', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                      >
                        ✕ Annuler le scan
                      </button>
                    </>
                  )}
                  
                  {!showScanner && (
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px' }}>
                      💡 Appuyez sur Entrée après la saisie manuelle
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={closeValidationModal}
                    style={{ padding: '12px 28px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={() => setValidationStep(1)}
                    style={{ padding: '12px 28px', backgroundColor: '#6c757d', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    ← Retour
                  </button>
                  <button
                    type="button"
                    onClick={handleScanCarte}
                    disabled={showScanner}
                    style={{ padding: '12px 28px', backgroundColor: showScanner ? '#dee2e6' : '#28a745', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: showScanner ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            )}

            {/* Étape 3 : Sélection opération */}
            {validationStep === 3 && (validationData.carte || validationModeGroupe) && (
              <div>
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#d4edda', borderRadius: '10px', border: '1px solid #28a745' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#155724' }}>
                    ✓ Opérateur : {validationData.operateur.prenom} {validationData.operateur.nom}
                  </div>
                  {validationModeGroupe ? (
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#155724', marginTop: '5px' }}>
                      ✓ Groupe : {validationData.typeProduitNom} ({validationData.cartesGroupe.length} {validationData.cartesGroupe.length > 1 ? 'cartes concernées' : 'carte concernée'})
                    </div>
                  ) : (
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#155724', marginTop: '5px' }}>
                      ✓ Carte : {validationData.carte.nom_attribue} ({validationData.carte.types_produits?.nom})
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '1px solid #dee2e6' }}>
                  {(() => {
                    // Récupérer les opérations selon le mode
                    let operations = [];
                    if (validationModeGroupe) {
                      // Mode groupé : récupérer les opérations du type de produit via la première carte
                      operations = validationData.cartesGroupe[0]?.operations || [];
                    } else {
                      // Mode individuel : utiliser les opérations de la carte
                      operations = validationData.carte?.operations || [];
                    }

                    // Calculer les compteurs pour le mode groupé
                    const getCompteurOperation = (operationId) => {
                      if (!validationModeGroupe) return null;
                      
                      const validees = validationData.cartesGroupe.filter(carte => 
                        carte.suivis?.some(s => s.operation_id === operationId && s.validee === true)
                      ).length;
                      
                      return {
                        validees,
                        total: validationData.cartesGroupe.length
                      };
                    };

                    // Déterminer les opérations non validées
                    const operationsNonValidees = operations.filter(op => {
                      if (validationModeGroupe) {
                        const compteur = getCompteurOperation(op.id);
                        return compteur.validees < compteur.total; // Au moins une carte n'a pas validé
                      } else {
                        const suivi = validationData.carte.suivis.find(s => s.operation_id === op.id);
                        return !suivi?.validee;
                      }
                    }).map(op => op.id);

                    return (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>
                            ⚙️ Étape 3 : Sélection des opérations
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (validationData.operationIds.length === operationsNonValidees.length) {
                                setValidationData({ ...validationData, operationIds: [] });
                              } else {
                                setValidationData({ ...validationData, operationIds: operationsNonValidees });
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#007bff',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                          >
                            {validationData.operationIds.length === operationsNonValidees.length 
                              ? '☐ Tout désélectionner' 
                              : '☑ Tout sélectionner'}
                          </button>
                        </div>
                        <label style={{ display: 'block', color: '#495057', marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>
                          Quelles opérations venez-vous de terminer ?
                        </label>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {operations.map(operation => {
                            const compteur = getCompteurOperation(operation.id);
                            const isFullyValidated = compteur ? compteur.validees === compteur.total : false;
                            const isSelected = validationData.operationIds.includes(operation.id);
                            
                            // Pour mode individuel
                            const suiviIndividuel = !validationModeGroupe ? 
                              validationData.carte.suivis.find(s => s.operation_id === operation.id) : null;
                            const isValidatedIndividuel = suiviIndividuel?.validee;
                            
                            const isDisabled = validationModeGroupe ? isFullyValidated : isValidatedIndividuel;
                            
                            return (
                              <label
                                key={operation.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'start',
                                  padding: '12px',
                                  backgroundColor: isDisabled ? '#f0f0f0' : (isSelected ? '#e3f2fd' : '#ffffff'),
                                  border: `2px solid ${isDisabled ? '#dee2e6' : (isSelected ? '#28a745' : '#dee2e6')}`,
                                  borderRadius: '8px',
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  opacity: isDisabled ? 0.6 : 1
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setValidationData({ 
                                        ...validationData, 
                                        operationIds: [...validationData.operationIds, operation.id] 
                                      });
                                    } else {
                                      setValidationData({ 
                                        ...validationData, 
                                        operationIds: validationData.operationIds.filter(id => id !== operation.id) 
                                      });
                                    }
                                  }}
                                  disabled={isDisabled}
                                  style={{ 
                                    marginRight: '10px', 
                                    marginTop: '2px',
                                    width: '18px',
                                    height: '18px',
                                    flexShrink: 0,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    accentColor: '#28a745'
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', color: '#212529', fontSize: '14px' }}>
                                    {operation.nom}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                                    Durée estimée : {formatDuree(operation.temps_realisation)}
                                  </div>
                                  {validationModeGroupe && compteur && (
                                    <div style={{ 
                                      fontSize: '12px', 
                                      color: isFullyValidated ? '#28a745' : '#007bff', 
                                      marginTop: '4px',
                                      fontWeight: '600'
                                    }}>
                                      📊 {compteur.validees}/{compteur.total} carte{compteur.total > 1 ? 's' : ''} déjà validée{compteur.validees > 1 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                                {isDisabled && (
                                  <span style={{ color: '#28a745', fontSize: '14px', fontWeight: '600', flexShrink: 0, marginLeft: '10px' }}>
                                    ✓ {validationModeGroupe ? 'Complète' : 'Déjà validée'}
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>

                        {operations.filter(op => {
                          if (validationModeGroupe) {
                            const compteur = getCompteurOperation(op.id);
                            return compteur && compteur.validees < compteur.total;
                          } else {
                            const suivi = validationData.carte.suivis.find(s => s.operation_id === op.id);
                            return !suivi?.validee;
                          }
                        }).length === 0 && (
                          <div style={{ padding: '15px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', marginTop: '10px', color: '#856404', fontSize: '13px' }}>
                            ⚠️ Toutes les opérations ont déjà été validées.
                          </div>
                        )}
                        
                        {validationData.operationIds.length > 0 && (
                          <div style={{ padding: '12px', backgroundColor: '#d1ecf1', border: '1px solid #17a2b8', borderRadius: '8px', marginTop: '12px', color: '#0c5460', fontSize: '13px', fontWeight: '600' }}>
                            ✓ {validationData.operationIds.length} opération(s) sélectionnée(s)
                            {validationModeGroupe && ` • ${validationData.cartesGroupe.length} carte(s) seront traitées`}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={closeValidationModal}
                    style={{ padding: '12px 28px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    Annuler
                  </button>
                  {!validationModeGroupe && (
                    <button
                      type="button"
                      onClick={() => setValidationStep(2)}
                      style={{ padding: '12px 28px', backgroundColor: '#6c757d', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                    >
                      ← Retour
                    </button>
                  )}
                  {validationModeGroupe && (
                    <button
                      type="button"
                      onClick={() => setValidationStep(1)}
                      style={{ padding: '12px 28px', backgroundColor: '#6c757d', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                    >
                      ← Retour
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={validationModeGroupe ? handleValidateOperationGroupee : handleValidateOperation}
                    disabled={validationData.operationIds.length === 0}
                    style={{ padding: '12px 28px', backgroundColor: validationData.operationIds.length === 0 ? '#dee2e6' : '#28a745', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: validationData.operationIds.length === 0 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}
                  >
                    ✓ Valider {validationData.operationIds.length > 0 ? `${validationData.operationIds.length} opération(s)` : 'les opérations'}
                    {validationModeGroupe && validationData.cartesGroupe.length > 0 && ` pour ${validationData.cartesGroupe.length} carte(s)`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Progression Détaillée */}
      {showProgressModal && selectedCarteForProgress && (
        <div 
          onClick={() => setShowProgressModal(false)}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0, 0, 0, 0.6)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1000 
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              backgroundColor: '#ffffff', 
              borderRadius: '20px', 
              padding: '40px', 
              width: '700px', 
              maxWidth: '90%', 
              maxHeight: '90vh', 
              overflow: 'auto', 
              border: '2px solid #1aabf9',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* En-tête */}
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ 
                color: '#212529', 
                marginTop: 0, 
                marginBottom: '10px', 
                fontSize: '24px', 
                fontWeight: '600' 
              }}>
                📊 Progression Détaillée
              </h2>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#1aabf9', marginBottom: '5px' }}>
                {selectedCarteForProgress.nom_attribue}
              </div>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>
                {selectedCarteForProgress.types_produits?.nom} • Code : {selectedCarteForProgress.code_barre}
              </div>
            </div>

            {/* Statistiques globales */}
            <div style={{ 
              display: 'flex', 
              gap: '20px', 
              marginBottom: '40px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px'
            }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#1aabf9' }}>
                  {selectedCarteForProgress.suivis.filter(s => s.validee).length}
                </div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>
                  Opérations validées
                </div>
              </div>
              <div style={{ width: '1px', backgroundColor: '#dee2e6' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#adb5bd' }}>
                  {selectedCarteForProgress.suivis.filter(s => !s.validee).length}
                </div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>
                  En attente
                </div>
              </div>
              <div style={{ width: '1px', backgroundColor: '#dee2e6' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#28a745' }}>
                  {Math.round((selectedCarteForProgress.suivis.filter(s => s.validee).length / selectedCarteForProgress.suivis.length) * 100)}%
                </div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>
                  Complété
                </div>
              </div>
            </div>

            {/* Timeline des opérations */}
            <div style={{ position: 'relative' }}>
              {selectedCarteForProgress.suivis.map((suivi, index) => {
                const isValidated = suivi.validee;
                const isLast = index === selectedCarteForProgress.suivis.length - 1;
                
                return (
                  <div 
                    key={suivi.id}
                    style={{ 
                      position: 'relative',
                      paddingLeft: '60px',
                      paddingBottom: isLast ? '0' : '40px'
                    }}
                  >
                    {/* Ligne verticale */}
                    {!isLast && (
                      <div style={{
                        position: 'absolute',
                        left: '24px',
                        top: '40px',
                        bottom: '0',
                        width: '4px',
                        backgroundColor: isValidated ? '#28a745' : '#dee2e6',
                        transition: 'background-color 0.3s'
                      }} />
                    )}

                    {/* Rond de statut */}
                    <div style={{
                      position: 'absolute',
                      left: '0',
                      top: '0',
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: isValidated ? '#28a745' : '#dee2e6',
                      border: `4px solid ${isValidated ? '#d1fae5' : '#f8f9fa'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      transition: 'all 0.3s',
                      boxShadow: isValidated ? '0 4px 12px rgba(40, 167, 69, 0.3)' : 'none'
                    }}>
                      {isValidated ? '✓' : (
                        <span style={{ fontSize: '16px', color: '#6c757d' }}>
                          {index + 1}
                        </span>
                      )}
                    </div>

                    {/* Contenu de l'opération */}
                    <div style={{
                      backgroundColor: isValidated ? '#d1fae5' : '#f8f9fa',
                      borderRadius: '12px',
                      padding: '20px',
                      border: `2px solid ${isValidated ? '#28a745' : '#dee2e6'}`,
                      transition: 'all 0.3s'
                    }}>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#212529',
                        marginBottom: '8px'
                      }}>
                        {suivi.operations?.nom || 'Opération inconnue'}
                      </div>

                      {isValidated ? (
                        <>
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#155724',
                            marginBottom: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '16px' }}>👤</span>
                            <strong>Opérateur :</strong> {suivi.operateurs?.prenom} {suivi.operateurs?.nom}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#155724',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '16px' }}>🕐</span>
                            <strong>Validée le :</strong> {new Date(suivi.date_fin).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          {suivi.operations?.temps_realisation && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#6c757d',
                              marginTop: '8px',
                              fontStyle: 'italic'
                            }}>
                              Durée estimée : {formatDuree(suivi.operations.temps_realisation)}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#6c757d',
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '16px' }}>⏳</span>
                            En attente de validation
                          </div>
                          {suivi.operations?.temps_realisation && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#6c757d',
                              marginTop: '8px',
                              fontStyle: 'italic'
                            }}>
                              Durée estimée : {formatDuree(suivi.operations.temps_realisation)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bouton fermer */}
            <div style={{ marginTop: '40px', textAlign: 'center' }}>
              <button
                onClick={() => setShowProgressModal(false)}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#1aabf9',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1590d6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1aabf9'}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Report de cartes en retard */}
      {showReportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#212529',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              📅 Reporter les cartes en retard
            </h2>

            {cartesEnRetard.length === 0 ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#6c757d'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <p style={{ fontSize: '16px', fontWeight: '600' }}>
                  Aucune carte en retard !
                </p>
                <p style={{ fontSize: '14px', marginTop: '8px' }}>
                  Toutes les cartes sont à jour.
                </p>
              </div>
            ) : (
              <>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  border: '1px solid #f59e0b'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
                    <strong>⚠️ {cartesEnRetard.length} carte(s) non terminée(s)</strong> des semaines passées détectée(s).
                  </p>
                </div>

                {/* Liste des cartes */}
                <div style={{
                  marginBottom: '24px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #e0e5ea',
                  borderRadius: '8px'
                }}>
                  {cartesEnRetard.map(carte => (
                    <div
                      key={carte.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#212529' }}>
                          {carte.nom_attribue}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                          {carte.types_produits?.nom} • {carte.code_barre}
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 12px',
                        backgroundColor: '#fee2e2',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#dc2626'
                      }}>
                        Semaine {carte.semaine_planifiee}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Boutons d'action */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '24px'
                }}>
                  <button
                    onClick={() => handleReporterCartes('courante')}
                    style={{
                      flex: 1,
                      padding: '14px',
                      backgroundColor: '#1aabf9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1590d6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1aabf9'}
                  >
                    📌 Reporter à cette semaine
                  </button>
                  <button
                    onClick={() => handleReporterCartes('suivante')}
                    style={{
                      flex: 1,
                      padding: '14px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                  >
                    ⏭️ Reporter à la semaine suivante
                  </button>
                </div>
              </>
            )}

            {/* Bouton fermer */}
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#ced4da';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#dee2e6';
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modification Date d'Expédition */}
      {showDateModificationModal && carteEnCoursModification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            {!changementSemaine ? (
              <>
                {/* Étape 1 : Modification de la date */}
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#212529',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  📅 Modifier la date d'expédition
                </h2>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '4px' }}>
                    Carte
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#212529' }}>
                    {carteEnCoursModification.nom_attribue || carteEnCoursModification.types_produits?.nom}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                    {carteEnCoursModification.code_barre}
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#212529',
                    marginBottom: '8px'
                  }}>
                    Nouvelle date d'expédition :
                  </label>
                  <input
                    type="date"
                    value={nouvelleDate}
                    onChange={(e) => setNouvelleDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{
                  padding: '12px',
                  backgroundColor: '#e7f5ff',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  fontSize: '13px',
                  color: '#0c5ca5'
                }}>
                  ℹ️ La semaine de production sera recalculée automatiquement en fonction du délai
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={fermerModalDateModification}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: 'transparent',
                      color: '#6c757d',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleChangerDateExpedition}
                    disabled={!nouvelleDate}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: nouvelleDate ? '#1aabf9' : '#e9ecef',
                      color: nouvelleDate ? 'white' : '#adb5bd',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: nouvelleDate ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (nouvelleDate) e.currentTarget.style.backgroundColor = '#1590d6';
                    }}
                    onMouseLeave={(e) => {
                      if (nouvelleDate) e.currentTarget.style.backgroundColor = '#1aabf9';
                    }}
                  >
                    Valider
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Étape 2 : Confirmation changement de semaine */}
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#212529',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  ⚠️ Changement de semaine détecté
                </h2>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  border: '1px solid #f59e0b'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#856404', lineHeight: '1.6' }}>
                    En fonction du nouveau délai calculé, cette carte devrait être produite en <strong>semaine {nouvelleSemaine}</strong> 
                    (actuellement planifiée en semaine {carteEnCoursModification.semaine_planifiee}).
                  </p>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <div style={{ fontSize: '14px', marginBottom: '12px' }}>
                    <strong>Que souhaitez-vous faire ?</strong>
                  </div>
                  <div style={{ fontSize: '13px', color: '#6c757d', lineHeight: '1.6' }}>
                    • Déplacer la carte vers la semaine {nouvelleSemaine}<br />
                    • Garder en semaine {carteEnCoursModification.semaine_planifiee} et modifier uniquement la date
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => appliquerChangementDate(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: 'transparent',
                      color: '#6c757d',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    📌 Garder semaine {carteEnCoursModification.semaine_planifiee}
                  </button>
                  <button
                    onClick={() => appliquerChangementDate(true)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#1aabf9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1590d6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1aabf9'}
                  >
                    ⏭️ Déplacer en semaine {nouvelleSemaine}
                  </button>
                </div>

                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <button
                    onClick={fermerModalDateModification}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: '#6c757d',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      textDecoration: 'underline'
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notification rappel lundi matin */}
      {showRappelLundi && cartesEnRetard.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#ffffff',
          border: '3px solid #ef4444',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          zIndex: 2000,
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div style={{
              fontSize: '32px',
              animation: 'pulse 2s infinite'
            }}>
              🚨
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#ef4444',
                margin: '0 0 8px 0'
              }}>
                RAPPEL HEBDOMADAIRE
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#212529',
                margin: '0 0 12px 0',
                lineHeight: '1.5'
              }}>
                <strong>{cartesEnRetard.length} carte(s) non terminée(s)</strong> des semaines passées nécessite(nt) votre attention.
              </p>
              <p style={{
                fontSize: '13px',
                color: '#6c757d',
                margin: 0
              }}>
                Veuillez reporter ces cartes pour maintenir votre planning à jour.
              </p>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '16px'
          }}>
            <button
              onClick={() => {
                setShowRappelLundi(false);
                setShowReportModal(true);
              }}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
            >
              Voir les cartes
            </button>
            <button
              onClick={() => setShowRappelLundi(false)}
              style={{
                padding: '10px 16px',
                backgroundColor: 'transparent',
                color: '#6c757d',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.borderColor = '#ced4da';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#dee2e6';
              }}
            >
              Plus tard
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
      `}</style>

      {/* Panneau de commentaires */}
      {showCommentairesPanel && carteSelectionneeCommentaires && (
        <CommentairesPanel
          carte={carteSelectionneeCommentaires}
          operateur={operateurConnecte}
          onClose={handleFermerCommentaires}
        />
      )}
    </div>
  );
}

export default PlanningProductionV2;
